import * as React from "react"
import * as DialogPrimitive from "@radix-ui/react-dialog"
import { X } from "lucide-react"

import { cn } from "@/lib/utils"

const Dialog = DialogPrimitive.Root

const DialogTrigger = DialogPrimitive.Trigger

const DialogPortal = DialogPrimitive.Portal

const DialogClose = DialogPrimitive.Close

const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      "fixed inset-0 z-50 bg-black/80  data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
      className
    )}
    {...props}
  />
))
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName

const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content>
>(({ className, children, ...props }, ref) => (
  <DialogPortal>
    <DialogOverlay />
    <DialogPrimitive.Content
      ref={ref}
      className={cn(
        // O bg-background do shadcn NAO e o --color-bg do produto (no dark fica
        // ~4% mais claro e encosta no tom de painel, matando a hierarquia). Declarar
        // aqui na base conserta os ~35 modais de uma vez; quem precisar de outro
        // fundo continua sobrescrevendo pela className (tailwind-merge resolve).
        "fixed left-[50%] top-[50%] z-50 grid w-full max-w-lg translate-x-[-50%] translate-y-[-50%] gap-4 border border-[var(--color-border)] bg-[var(--color-bg)] p-6 shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] sm:rounded-lg",
        // Abaixo de md o modal vira folha ancorada embaixo (bottom sheet).
        //
        // Aqui na base, e nao em cada consumidor, porque o defeito e do
        // primitivo: uma caixa centralizada com `max-w` fixo, em 375px, ou
        // aparece com margem ridicula ou estoura. Sao ~35 modais no produto e
        // consertar um a um garantiria que o proximo nasceria errado de novo.
        //
        // Folha e nao tela cheia, e a diferenca importa: com altura forcada, uma
        // confirmacao de quatro linhas passava a ocupar a tela inteira com um
        // vazio enorme embaixo do rodape. A folha pega a altura do conteudo,
        // entao modal curto fica curto e modal longo cresce ate 92dvh e rola por
        // dentro. E o padrao que Trello, Asana e Linear usam no celular.
        //
        // `dvh` e nao `vh`: no celular a barra de endereco entra e sai, e `vh`
        // congela no maior valor, o que deixaria o rodape do modal (onde ficam
        // os botoes) escondido atras dela.
        //
        // Ancorar embaixo tambem resolve o notch de graca: a folha nunca comeca
        // no topo da tela, entao o X de fechar (que e `absolute` e nao herda o
        // padding de safe-area) nao cai atras da Dynamic Island.
        //
        // O `!` e obrigatorio, nao estilo: `left-[50%]` e `max-md:left-0` tem a
        // mesma especificidade, entao sem ele quem ganha e a ordem de emissao no
        // CSS, nao a media query. Mesma receita que a gaveta da Sidebar ja usa.
        // `bottom` sai do zero e passa a ser a altura do teclado: sem isto, a
        // folha continua ancorada no rodape da tela e o teclado a cobre por
        // baixo, escondendo justamente os botoes de confirmar. A variavel vale
        // 0 quando nao ha teclado (e no Android, onde o
        // `interactive-widget=resizes-content` do index.html ja encolheu o
        // layout), entao nada e compensado duas vezes.
        //
        // A altura maxima desconta o mesmo valor: sem isso a folha manteria
        // 92dvh e o topo dela sairia pela borda de cima da tela.
        "max-md:!left-0 max-md:!right-0 max-md:!top-auto max-md:!bottom-[var(--altura-teclado,0px)]",
        "max-md:!w-full max-md:!max-w-none max-md:!translate-x-0 max-md:!translate-y-0",
        "max-md:!max-h-[calc(92dvh_-_var(--altura-teclado,0px))] max-md:overflow-y-auto",
        "max-md:!rounded-b-none max-md:!rounded-t-2xl",
        "max-md:pb-[env(safe-area-inset-bottom)]",
        className
      )}
      {...props}
    >
      {children}
      {/* O alvo de toque é 44px abaixo de `md`, e o ícone continua 16px.

          A caixa era do tamanho do ícone, 16x16, porque o `<button>` chega aqui
          sem padding (o reset de `@layer base` zera o do navegador). Num modal
          de desktop isso passa: há Cancelar, Esc e clique no fundo. Abaixo de
          `md` o DialogContent vira folha, e em 28/08/2026 a folha de seções do
          Ajustes tornou este X o caminho principal de fechar no dedo, o que
          expôs os 16px contra o piso de 44px da escada do DESIGN_SYSTEM.

          `flex items-center justify-center` para o ícone não descolar do centro
          quando a caixa cresce. */}
      <DialogPrimitive.Close className="absolute right-4 top-4 flex items-center justify-center rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground max-md:h-11 max-md:w-11 max-md:right-2 max-md:top-2">
        <X className="h-4 w-4" />
        <span className="sr-only">Close</span>
      </DialogPrimitive.Close>
    </DialogPrimitive.Content>
  </DialogPortal>
))
DialogContent.displayName = DialogPrimitive.Content.displayName

const DialogHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "flex flex-col space-y-1.5 text-center sm:text-left",
      className
    )}
    {...props}
  />
)
DialogHeader.displayName = "DialogHeader"

const DialogFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2",
      className
    )}
    {...props}
  />
)
DialogFooter.displayName = "DialogFooter"

const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn(
      "text-lg font-semibold leading-none tracking-tight",
      className
    )}
    {...props}
  />
))
DialogTitle.displayName = DialogPrimitive.Title.displayName

const DialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn("text-sm text-muted-foreground", className)}
    {...props}
  />
))
DialogDescription.displayName = DialogPrimitive.Description.displayName

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogTrigger,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
}
